import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Instagram, Facebook, ExternalLink } from 'lucide-react';
import { legalConfig } from '../config/legal';

const STUDIO_KEYS = ['beaubourg', 'saintHonore', 'neuilly', 'keller', 'wildcat'] as const;
const SCHEDULE_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

export function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const isAccountRoute = location.pathname.startsWith('/account');
  const isBookingRoute =
    location.pathname === '/book-piercing' || location.pathname === '/book-tattoo';

  const socialLinks = [
    { icon: Instagram, href: 'https://www.instagram.com/abraxasparis/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/abraxasparis/', label: 'Facebook' },
  ];

  // Compact legal strip during reservation flows — avoids landing on the tall brand band
  // after a step change when the new step content is shorter than the previous one.
  if (isBookingRoute) {
    return (
      <footer className="bg-black text-white border-t border-primary-500/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p className="text-xs text-white/40 tracking-wide">
              © {new Date().getFullYear()} Abraxas — {t('footer.byline')}
            </p>
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs uppercase tracking-wider text-white/50">
              <Link to="/legal/privacy" className="hover:text-heading transition-colors">{t('footer.privacy')}</Link>
              <Link to="/legal/terms" className="hover:text-heading transition-colors">{t('footer.terms')}</Link>
              <Link to="/legal/cookies" className="hover:text-heading transition-colors">{t('footer.cookies')}</Link>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className={`bg-black text-white border-t-2 border-primary-500 ${isAccountRoute ? 'lg:pl-64' : ''}`}>
      {/* Brand band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-white/50 mb-3">
                {t('footer.byline')}
              </p>
              <p className="text-4xl sm:text-5xl md:text-6xl font-medium tracking-[0.12em] text-heading leading-none">
                {t('footer.brand')}
              </p>
              <p className="mt-4 text-lg sm:text-xl font-medium tracking-wide text-white/90 italic">
                {t('footer.slogan')}
              </p>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3">
              <span className="inline-block border border-white/30 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/80">
                {t('footer.since')}
              </span>
              <a
                href="https://abraxas.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-wider text-primary-500 hover:text-heading transition-colors"
              >
                {t('footer.discover')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Content grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Contact */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-heading font-medium border-l-2 border-primary-500 pl-3 mb-5">
              {t('footer.contact')}
            </h4>
            <div className="space-y-4 text-sm text-white/75">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-500" />
                <div className="leading-relaxed">
                  <p>9 rue Saint-Merri</p>
                  <p>75004 Paris, France</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 flex-shrink-0 text-primary-500" />
                <a href="tel:+33148043355" className="hover:text-heading transition-colors">
                  +33 1 48 04 33 55
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 flex-shrink-0 text-primary-500" />
                <a href="mailto:abraxas@abraxas.fr" className="hover:text-heading transition-colors">
                  abraxas@abraxas.fr
                </a>
              </div>
            </div>
          </div>

          {/* Studios */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-heading font-medium border-l-2 border-primary-500 pl-3 mb-5">
              {t('footer.studios')}
            </h4>
            <p className="text-xs text-white/50 mb-4 uppercase tracking-wider">
              {t('footer.studiosSubtitle')}
            </p>
            <ul className="space-y-2 text-sm text-white/75">
              {STUDIO_KEYS.map((key) => (
                <li key={key} className="flex items-center gap-2">
                  <span className="w-1 h-1 bg-primary-500 flex-shrink-0" />
                  {t(`footer.studioList.${key}`)}
                </li>
              ))}
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-heading font-medium border-l-2 border-primary-500 pl-3 mb-5">
              {t('footer.hours')}
            </h4>
            <div className="space-y-1.5 text-sm text-white/75">
              {SCHEDULE_KEYS.map((key) => (
                <p key={key} >
                  {t(`footer.schedule.${key}`)}
                </p>
              ))}
            </div>
          </div>

          {/* Newsletter & social */}
          <div>
            <h4 className="text-sm uppercase tracking-[0.2em] text-heading font-medium border-l-2 border-primary-500 pl-3 mb-5">
              {t('footer.newsletterTitle')}
            </h4>
            <p className="text-sm text-white/60 mb-4 leading-relaxed">
              {t('footer.newsletterSubtitle')}
            </p>
            <div className="space-y-2 mb-6">
              <input
                type="email"
                placeholder={t('footer.emailPlaceholder')}
                className="w-full px-4 py-2.5 bg-black/40 border border-white/30 text-white placeholder-white/40 focus:outline-none focus:border-primary-500 transition-colors"
              />
              <button type="button" className="w-full btn-primary">
                {t('footer.subscribe')}
              </button>
            </div>
            <p className="text-xs uppercase tracking-[0.15em] text-white/50 mb-3">
              {t('footer.follow')}
            </p>
            <div className="flex gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-10 h-10 border border-white/30 text-white/70 hover:bg-primary-500 hover:border-primary-500 hover:text-white transition-all duration-200"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-white/40 tracking-wide">
              © {new Date().getFullYear()} Abraxas — {t('footer.byline')}. {t('footer.rights')}
            </p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs uppercase tracking-wider text-white/50">
              <Link to="/legal/privacy" className="hover:text-heading transition-colors">{t('footer.privacy')}</Link>
              <Link to="/legal/terms" className="hover:text-heading transition-colors">{t('footer.terms')}</Link>
              <Link to="/legal/cookies" className="hover:text-heading transition-colors">{t('footer.cookies')}</Link>
              <a href={`mailto:${legalConfig.privacyContactEmail}`} className="hover:text-heading transition-colors">
                {legalConfig.privacyContactEmail}
              </a>
              <Link to="/quick-client-create" className="hover:text-heading transition-colors">
                {t('navigation.book')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
